import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  SimpleGrid,
  Image,
  Text,
  HStack,
  Spinner,
  Center,
  Select,
  Badge,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { SportsPageShell } from './ui/SportsPageShell';
import { SportsCard } from './ui/SportsCard';
import { MatchScoreHeader } from './ui/MatchScoreHeader';
import { ResponsiveTableContainer } from './ui/ResponsiveTableContainer';
import { useAppColors } from '../hooks/useAppColors';
import { rankingService } from '../services/rankingService';
import { hydrateMundialMatchesAsync } from '../services/mundial/mundialKnockoutHydrationService';
import { useLeague } from '../context/LeagueContext';
import type { LeagueId, Match as AppMatch } from '../types';
import {
  getActiveMundialJornadas,
  getCurrentMundialGroupJornada,
  getJornadaLabel,
  getMundialGroupMatches,
  groupMatchesForDisplay,
  filterMatchesByMundialJornada,
  isMatchPredictionOpen,
  parseLigaMxJornadaNumber,
  toDate,
} from '../services/matchdayService';
import {
  calculateOverallPoints,
  computeStageLeaderboards,
  computeStageWinners,
  StageLeaderboard,
  StageWinnerResult,
} from '../services/mundial/mundialStageRankingService';
import { filterParticipatingUsers, toRankingParticipants } from '../services/userParticipationService';
import { buildUserPointBreakdown, computeLigaMxStageLeaderboards } from '../services/scoringService';
import { ParticipantPointsModal } from './rankings/ParticipantPointsModal';
import { StageAccumulationSection } from './rankings/StageAccumulationSection';

interface Match {
  id: string;
  league?: LeagueId;
  team_a: string;
  team_b: string;
  team_a_code?: string | null;
  team_b_code?: string | null;
  score_a: number | null;
  score_b: number | null;
  phase: string;
  activo: boolean;
  team_a_logo: string;
  team_b_logo: string;
  group?: string;
  match_type?: string;
  bracket_slot?: string | null;
  source_slot_a?: string | null;
  source_slot_b?: string | null;
  jornada?: number | null;
  date?: Date;
}

interface Prediction {
  id: string;
  user_id: string;
  match_id: string;
  league?: LeagueId;
  predicted_score_a: number;
  predicted_score_b: number;
}

interface User {
  id: string;
  uuid: string;
  name: string;
  email: string;
  predictions: Prediction[];
  participacion?: Partial<Record<LeagueId, boolean>>;
}

interface DisplaySection {
  key: string;
  label: string;
  matches: Match[];
}

interface MundialJornadaBlock {
  jornada: number;
  sections: DisplaySection[];
}

interface Ranking {
  uuid: string;
  name: string;
  points: number;
}

interface RankingDataContext {
  predictions: Prediction[];
  matchesById: Record<string, Match>;
}

const getUserAvatar = (name: string): string | undefined => {
  switch (name) {
    case 'Ramiro':
      return '/images/ramiro.png';
    case 'Gus':
      return '/images/el_gus.png';
    case 'Neto':
      return '/images/neto2.png';
    default:
      return undefined;
  }
};

const getPhaseLabel = (phase: string, league: LeagueId) => {
  if (league === 'mundial_fifa' && phase.startsWith('Grupo')) {
    return phase;
  }
  const jornadaNum = parseLigaMxJornadaNumber(phase);
  if (jornadaNum) return `Jornada ${jornadaNum}`;
  switch (phase) {
    case 'Quarters':
      return 'Cuartos de Final';
    case 'Semifinals':
      return 'Semifinales';
    case 'Final':
      return 'Final';
    default:
      return phase;
  }
};

const removeDuplicatePredictions = async (predictions: Prediction[]) => {
  const seen = new Set<string>();
  const toDelete: string[] = [];

  predictions.forEach(pred => {
    const key = `${pred.user_id}_${pred.match_id}`;
    if (seen.has(key)) {
      toDelete.push(pred.id);
    } else {
      seen.add(key);
    }
  });

  for (const id of toDelete) {
    try {
      await deleteDoc(doc(db, "predictions", id));
    } catch (e) {
      console.error(`Error eliminando predicción duplicada ${id}:`, e);
    }
  }
};

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

const Rankings = () => {
  const { league: contextLeague } = useLeague();
  const [selectedLeague, setSelectedLeague] = useState<LeagueId>(contextLeague || 'liga_mx');
  const league = selectedLeague;
  const [, setUsers] = useState<User[]>([]);
  const [, setUserPoints] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [overallRankings, setOverallRankings] = useState<Ranking[]>([]);
  const [currentPhase] = useState<string>('');
  const [matches, setMatches] = useState<{[key: string]: Match[]}>({});
  const [mundialJornadaBlocks, setMundialJornadaBlocks] = useState<MundialJornadaBlock[]>([]);
  const [stageWinners, setStageWinners] = useState<StageWinnerResult[]>([]);
  const [stageLeaderboards, setStageLeaderboards] = useState<StageLeaderboard[]>([]);
  const [teamLogos, setTeamLogos] = useState<{ [teamName: string]: string }>({});
  const [rankingContext, setRankingContext] = useState<RankingDataContext | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<Ranking | null>(null);
  const [selectedStage, setSelectedStage] = useState<StageLeaderboard | null>(null);
  const { isOpen: isPointsModalOpen, onOpen: onPointsModalOpen, onClose: onPointsModalClose } = useDisclosure();
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const calculatePoints = (prediction: Prediction, match: Match): number => {
    if (match.score_a === null || match.score_b === null) return 0;

    let points = 0;

    // 1. Marcador exacto (3 puntos)
    if (prediction.predicted_score_a === match.score_a && 
        prediction.predicted_score_b === match.score_b) {
      points = 3;
      return points; // Si acierta el marcador exacto, no necesitamos revisar más
    }

    // 2. Empate (1 punto)
    if (match.score_a === match.score_b && 
        prediction.predicted_score_a === prediction.predicted_score_b) {
      points = 1;
      return points;
    }

    // 3. Ganador correcto (1 punto)
    const matchResult = Math.sign(match.score_a - match.score_b);
    const predictionResult = Math.sign(prediction.predicted_score_a - prediction.predicted_score_b);
    
    if (matchResult === predictionResult && matchResult !== 0) {
      points = 1;
    }

    return points;
  };

  const calculatePointsForUser = (
    userPreds: Prediction[],
    matchesById: { [key: string]: Match }
  ) => {
    let points = 0;

    // Suma puntos normales para todas las predicciones
    userPreds.forEach(pred => {
      const match = matchesById[pred.match_id];
      if (match) {
        points += calculatePoints(pred, match);
      }
    });

    // Suma puntos extra por etapa si corresponde
    const etapas = ['cuartos', 'semifinal', 'final'];
    etapas.forEach(etapa => {
      // Encuentra todos los partidos de la etapa en matchesById
      const partidosEtapa = Object.values(matchesById).filter(m =>
        m.phase && m.phase.toLowerCase().includes(etapa)
      );

      // Deben existir exactamente 2 partidos (ida y vuelta) para la etapa
      if (partidosEtapa.length !== 2) return;

      // Solo si TODOS los partidos tienen resultado real
      const todosJugados = partidosEtapa.every(m => m.score_a !== null && m.score_b !== null);

      // Predicciones del usuario para la etapa
      const predsEtapa = userPreds.filter(p => {
        const match = matchesById[p.match_id];
        return match && match.phase && match.phase.toLowerCase().includes(etapa);
      });

      // Solo si el usuario tiene predicción para TODOS los partidos de la etapa
      if (todosJugados && predsEtapa.length === 2) {
        // Suma goles reales y pronosticados
        const sumaGolesReales = partidosEtapa.reduce(
          (acc, m) => acc + (m.score_a ?? 0) + (m.score_b ?? 0), 0
        );
        const sumaGolesPronosticados = predsEtapa.reduce(
          (acc, p) => acc + (p.predicted_score_a ?? 0) + (p.predicted_score_b ?? 0), 0
        );

        if (sumaGolesReales === sumaGolesPronosticados && sumaGolesReales > 0) {
          points += 2;
        }
      }
    });

    return points;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // 1. Cargar todos los usuarios
      const usersSnap = await getDocs(collection(db, 'users'));
      const allUsersData = usersSnap.docs.map(doc => ({
        id: doc.id,
        uuid: doc.data().uuid,
        ...doc.data()
      })) as User[];
      const usersData = filterParticipatingUsers(allUsersData, league);
      const rankingUsers = toRankingParticipants(usersData);
      setUsers(usersData);

      // 2. Cargar todas las predicciones y filtrar por liga
      const predictionsSnap = await getDocs(collection(db, 'predictions'));
      const allPredictions = predictionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Prediction[];
      const predictions = allPredictions.filter(p =>
        p.league === league || (league === 'liga_mx' && !p.league)
      );

      // Elimina duplicados en Firestore
      await removeDuplicatePredictions(predictions);

      // Filtrar duplicados: solo la primera predicción por usuario y partido
      const uniquePredictionsMap = new Map<string, Prediction>();
      predictions.forEach(pred => {
        const key = `${pred.user_id}_${pred.match_id}`;
        if (!uniquePredictionsMap.has(key)) {
          uniquePredictionsMap.set(key, pred);
        }
      });
      const uniquePredictions = Array.from(uniquePredictionsMap.values());

      // 3. Cargar todos los partidos, filtrar por liga y agrupar por fase
      const matchesRef = collection(db, 'matches');
      const matchesSnapshot = await getDocs(matchesRef);
      const matchesById: { [key: string]: Match } = {};
      const matchesByPhase: { [key: string]: Match[] } = {};

      matchesSnapshot.forEach(doc => {
        const data = doc.data();
        const match: Match = {
          id: String(doc.id),
          league: data.league,
          team_a: data.team_a,
          team_b: data.team_b,
          team_a_code: data.team_a_code,
          team_b_code: data.team_b_code,
          score_a: data.score_a !== null ? Number(data.score_a) : null,
          score_b: data.score_b !== null ? Number(data.score_b) : null,
          phase: data.phase,
          activo: Boolean(data.activo),
          team_a_logo: data.team_a_logo,
          team_b_logo: data.team_b_logo,
          group: data.group,
          match_type: data.match_type,
          bracket_slot: data.bracket_slot,
          source_slot_a: data.source_slot_a,
          source_slot_b: data.source_slot_b,
          jornada: data.jornada ?? null,
          date: toDate(data.date),
        };
        const matchLeague = match.league || 'liga_mx';
        if (matchLeague !== league) return;
        matchesById[match.id] = match;
        if (!matchesByPhase[match.phase]) matchesByPhase[match.phase] = [];
        matchesByPhase[match.phase].push(match);
      });

      if (league === 'mundial_fifa') {
        const hydrated = await hydrateMundialMatchesAsync(
          Object.values(matchesById) as AppMatch[]
        );
        hydrated.forEach((m) => {
          if (m.id) matchesById[m.id] = m as unknown as Match;
        });
        Object.keys(matchesByPhase).forEach((phase) => {
          delete matchesByPhase[phase];
        });
        hydrated.forEach((m) => {
          if (!matchesByPhase[m.phase]) matchesByPhase[m.phase] = [];
          matchesByPhase[m.phase].push(m as unknown as Match);
        });
      }

      // 4. Calcular puntos por usuario (todos los partidos con resultado; activo no afecta)
      const points: Record<string, number> = {};
      if (league === 'mundial_fifa') {
        const allMundialMatches = Object.values(matchesById) as AppMatch[];

        rankingUsers.forEach((user) => {
          points[user.uuid] = calculateOverallPoints(
            user.uuid,
            uniquePredictions,
            matchesById
          );
        });
        setStageWinners(
          computeStageWinners(rankingUsers, uniquePredictions, allMundialMatches)
        );
        setStageLeaderboards(
          computeStageLeaderboards(rankingUsers, uniquePredictions, allMundialMatches)
        );
      } else {
        rankingUsers.forEach((user) => {
          const userPreds = uniquePredictions.filter((p) => p.user_id === user.uuid);
          points[user.uuid] = calculatePointsForUser(userPreds, matchesById);
        });
        setStageWinners([]);
        setStageLeaderboards(
          computeLigaMxStageLeaderboards(rankingUsers, uniquePredictions, matchesById)
        );
      }
      setUserPoints(points);
      setRankingContext({ predictions: uniquePredictions, matchesById });

      // Rankings - usar todos los usuarios activos de la base de datos
      const rankings = rankingUsers
        .map((user) => ({
          uuid: user.uuid,
          name: user.name,
          points: points[user.uuid] || 0,
        }))
        .sort((a, b) => b.points - a.points);

      setRankings(rankings);
      setOverallRankings(rankings);

      if (league === 'mundial_fifa') {
        const allFlat = Object.values(matchesById) as AppMatch[];
        const groupPool = getMundialGroupMatches(allFlat);
        const activeJornadas = getActiveMundialJornadas(groupPool);
        const jornadasToShow =
          activeJornadas.length > 0
            ? activeJornadas
            : [getCurrentMundialGroupJornada(groupPool)];
        const blocks = jornadasToShow.map((jornada) => {
          const jornadaMatches = filterMatchesByMundialJornada(
            groupPool,
            jornada,
            groupPool
          );
          const sections = groupMatchesForDisplay(
            jornadaMatches as AppMatch[],
            'mundial_fifa',
            groupPool
          ).map((section) => ({
            key: section.key,
            label: section.label,
            matches: section.matches as Match[],
          }));
          return { jornada, sections };
        });
        setMundialJornadaBlocks(blocks);
        setMatches({});
      } else {
        setMundialJornadaBlocks([]);
        setMatches(matchesByPhase);
      }

      setLoading(false);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  useEffect(() => {
    const fetchLogos = async () => {
      const logos: { [teamName: string]: string } = {};
      const matchList =
        rankingContext
          ? Object.values(rankingContext.matchesById)
          : league === 'mundial_fifa'
          ? mundialJornadaBlocks.flatMap((block) =>
              block.sections.flatMap((s) => s.matches)
            )
          : Object.values(matches).flat();
      for (const match of matchList) {
        if (match.team_a_logo) logos[match.team_a] = match.team_a_logo;
        if (match.team_b && match.team_b_logo) logos[match.team_b] = match.team_b_logo;
        if (match.team_a && !logos[match.team_a]) {
          logos[match.team_a] = (await rankingService.getTeamLogo(match.team_a)) || '';
        }
        if (match.team_b && !logos[match.team_b]) {
          logos[match.team_b] = (await rankingService.getTeamLogo(match.team_b)) || '';
        }
      }
      setTeamLogos(logos);
    };
    const hasMatches =
      rankingContext
        ? Object.keys(rankingContext.matchesById).length > 0
        : league === 'mundial_fifa'
        ? mundialJornadaBlocks.length > 0
        : Object.keys(matches).length > 0;
    if (hasMatches) {
      fetchLogos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, mundialJornadaBlocks, league, rankingContext]);

  const { subtle, accentStrong } = useAppColors();
  const podiumBg = useColorModeValue('gold.50', 'rgba(229, 160, 32, 0.12)');
  const podiumRankColor = useColorModeValue('pitch.700', 'pitch.200');
  const selectBorder = useColorModeValue('pitch.300', 'gray.600');
  const stageBorder = useColorModeValue('gray.200', 'gray.600');
  const zoneColor = (index: number, total: number) => {
    if (index === 0) return 'gold.500';
    if (index === 1) return 'pitch.500';
    if (index === 2) return 'pitch.400';
    if (index >= total - 2) return 'red.400';
    return 'transparent';
  };

  const selectedBreakdown = useMemo(() => {
    if (!selectedParticipant || !rankingContext) return [];
    const items = buildUserPointBreakdown(
      selectedParticipant.uuid,
      rankingContext.predictions,
      rankingContext.matchesById,
      { includeStageBonuses: league === 'liga_mx' }
    );
    if (!selectedStage) return items;

    if (league === 'liga_mx') {
      return items.filter((item) => item.phase === selectedStage.id);
    }

    return items.filter((item) => {
      const match = item.match;
      if (!match) return item.reason === 'stage_goals_bonus';
      return selectedStage.matchFilter(match as AppMatch);
    });
  }, [selectedParticipant, rankingContext, league, selectedStage]);

  const selectedBreakdownPoints = selectedStage
    ? selectedBreakdown.reduce((sum, item) => sum + item.points, 0)
    : selectedParticipant?.points ?? 0;

  if (loading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="pitch.500" />
      </Center>
    );
  }

  const activePhases = Object.entries(matches).filter(
    ([phase, phaseMatches]) =>
      phase && phaseMatches.some((m) => m.activo)
  );

  const handleParticipantClick = (participant: Ranking, stage?: StageLeaderboard | null) => {
    setSelectedParticipant(participant);
    setSelectedStage(stage ?? null);
    onPointsModalOpen();
  };

  const handlePointsModalClose = () => {
    onPointsModalClose();
    setSelectedParticipant(null);
    setSelectedStage(null);
  };

  const renderMatchCards = (matchList: Match[]) => (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      {matchList.map((match) => {
        const open =
          match.score_a == null &&
          match.score_b == null &&
          (match.date ? isMatchPredictionOpen(match as AppMatch) : true);
        return (
          <SportsCard key={match.id}>
            <MatchScoreHeader
              teamA={match.team_a || ''}
              teamB={match.team_b || ''}
              scoreA={match.score_a}
              scoreB={match.score_b}
              teamLogos={teamLogos}
              logoSize="28px"
            />
            <Text
              mt={2}
              fontSize="xs"
              textAlign="center"
              color={open ? accentStrong : subtle}
              fontWeight={open ? 'semibold' : 'normal'}
            >
              {open ? 'Pronósticos abiertos' : 'Pronósticos cerrados'}
            </Text>
          </SportsCard>
        );
      })}
    </SimpleGrid>
  );

  const leagueSelect = (
    <Select
      value={league}
      onChange={(e) => setSelectedLeague(e.target.value as LeagueId)}
      w={{ base: 'full', md: '280px' }}
      maxW={{ base: 'full', md: '280px' }}
      size="sm"
      borderColor={selectBorder}
    >
      <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
      <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
    </Select>
  );

  return (
    <SportsPageShell
      title="Tabla de posiciones"
      subtitle={`Puntos y resultados — ${LEAGUE_NAMES[league]}`}
      badge="RANKING"
      actions={leagueSelect}
    >
      <VStack spacing={6} align="stretch">
        {currentPhase && rankings.length > 0 && (
          <SportsCard>
            <Badge mb={3} colorScheme="pitch">
              Jornada / fase actual — {getPhaseLabel(currentPhase, league)}
            </Badge>
            <ResponsiveTableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th w="48px">#</Th>
                  <Th>Participante</Th>
                  <Th isNumeric>Pts</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rankings.map((user, index) => (
                  <Tr
                    key={user.name}
                    bg={index < 3 ? podiumBg : undefined}
                  >
                    <Td fontWeight="bold" color={index < 3 ? podiumRankColor : undefined}>
                      {index + 1}
                    </Td>
                    <Td>
                      <HStack>
                        <Text fontWeight={index < 3 ? 'bold' : 'medium'}>{user.name}</Text>
                        {getUserAvatar(user.name) && (
                          <Image
                            src={getUserAvatar(user.name)}
                            alt=""
                            boxSize={user.name === 'Gus' ? '32px' : '26px'}
                          />
                        )}
                      </HStack>
                    </Td>
                    <Td isNumeric fontWeight="bold">
                      {user.points}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </ResponsiveTableContainer>
          </SportsCard>
        )}

        <SportsCard accent>
          <Badge mb={3} bg="gold.500" color="slate.900">
            Acumulado general
          </Badge>
          {league === 'mundial_fifa' && (
            <Text fontSize="xs" color={subtle} mb={3}>
              Los puntos se conservan aunque una jornada se desactive en administración.
            </Text>
          )}
          <Text fontSize="xs" color={subtle} mb={3}>
            Toca un participante para ver en qué partidos obtuvo sus puntos.
          </Text>
          <ResponsiveTableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th w="6px" />
                <Th w="48px">#</Th>
                <Th>Participante</Th>
                <Th isNumeric>Pts totales</Th>
              </Tr>
            </Thead>
            <Tbody>
              {overallRankings.map((user, index) => (
                <Tr
                  key={user.uuid}
                  cursor="pointer"
                  onClick={() => handleParticipantClick(user)}
                  _hover={{ bg: rowHoverBg }}
                  transition="background 0.15s"
                >
                  <Td p={0} pl={1}>
                    <Box
                      w="3px"
                      h="full"
                      minH="32px"
                      bg={zoneColor(index, overallRankings.length)}
                      borderRadius="full"
                    />
                  </Td>
                  <Td fontWeight="bold">{index + 1}</Td>
                  <Td>
                    <HStack>
                      <Text fontWeight={index < 3 ? 'bold' : 'medium'}>{user.name}</Text>
                      {getUserAvatar(user.name) && (
                        <Image
                          src={getUserAvatar(user.name)}
                          alt=""
                          boxSize={user.name === 'Gus' ? '32px' : '26px'}
                        />
                      )}
                    </HStack>
                  </Td>
                  <Td isNumeric fontWeight="bold" fontSize="md">
                    {user.points}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </ResponsiveTableContainer>
        </SportsCard>

        {stageLeaderboards.length > 0 && (
          <StageAccumulationSection
            stages={stageLeaderboards}
            getUserAvatar={getUserAvatar}
            onParticipantClick={(participant, stage) =>
              handleParticipantClick(
                {
                  uuid: participant.uuid,
                  name: participant.name,
                  points: participant.points,
                },
                stage
              )
            }
          />
        )}

        {league === 'mundial_fifa' && stageWinners.length > 0 && (
          <SportsCard>
            <Badge mb={3} colorScheme="pitch">
              Ganadores por etapa
            </Badge>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {stageWinners.map((stage) => (
                <Box
                  key={stage.id}
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={stageBorder}
                >
                  <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                    <Text fontWeight="bold" fontSize="sm">
                      {stage.title}
                    </Text>
                    <Badge colorScheme="gold">{stage.prizePct} del pozo</Badge>
                  </HStack>
                  {stage.winners.length === 0 ? (
                    <Text fontSize="sm" color={subtle}>
                      Sin datos aún
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={1}>
                      {stage.winners.map((w) => (
                        <HStack key={w.userId} justify="space-between">
                          <HStack>
                            {getUserAvatar(w.name) && (
                              <Image src={getUserAvatar(w.name)} alt="" boxSize="22px" />
                            )}
                            <Text fontWeight="semibold" fontSize="sm">
                              {w.name}
                            </Text>
                            {stage.tied && stage.isComplete && (
                              <Badge size="sm" colorScheme="orange">
                                Empate
                              </Badge>
                            )}
                            {!stage.isComplete && (
                              <Badge size="sm" colorScheme="gray">
                                Parcial
                              </Badge>
                            )}
                          </HStack>
                          <Text fontWeight="bold" fontSize="sm">
                            {w.points} pts
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          </SportsCard>
        )}

        {league === 'mundial_fifa' &&
          mundialJornadaBlocks.map(({ jornada, sections }) => (
          <Box key={jornada}>
            <Badge mb={3} colorScheme="pitch" fontSize="sm">
              {getJornadaLabel('mundial_fifa', jornada)}
            </Badge>
            {sections.length === 0 ? (
              <Text color={subtle} fontSize="sm">
                No hay partidos registrados para esta jornada.
              </Text>
            ) : (
              <>
                <Text fontSize="xs" color={subtle} mb={3}>
                  {sections.reduce((n, s) => n + s.matches.length, 0)} partidos en{' '}
                  {getJornadaLabel('mundial_fifa', jornada)} (todos los del calendario,
                  estén o no activos en admin).
                </Text>
                {sections.map((section) => (
                <Box key={section.key} mb={6}>
                  <Badge mb={3} variant="outline" colorScheme="pitch">
                    {section.label}
                  </Badge>
                  {renderMatchCards(section.matches)}
                </Box>
              ))}
              </>
            )}
          </Box>
        ))}

        {league === 'liga_mx' &&
          activePhases.map(([phase, phaseMatches]) => (
          <Box key={phase}>
            <Badge mb={3} colorScheme="pitch" fontSize="sm">
              {getPhaseLabel(phase, league)}
            </Badge>
            {renderMatchCards(phaseMatches.filter((m) => m.activo))}
          </Box>
        ))}
      </VStack>

      <ParticipantPointsModal
        isOpen={isPointsModalOpen}
        onClose={handlePointsModalClose}
        participantName={selectedParticipant?.name ?? ''}
        totalPoints={selectedBreakdownPoints}
        stageTitle={selectedStage?.title}
        items={selectedBreakdown}
        league={league}
        teamLogos={teamLogos}
      />
    </SportsPageShell>
  );
};

export default Rankings; 